import { gAgentProfilesEnabled } from "@coreapp/whitelabel/brand";
import { isBlockedField } from "@coreapp/utils/FormatFieldValue";

/**
 * TEMP INS-7: agent profiles ship isolved-only via the per-brand
 * gAgentProfilesEnabled flag (brand_default.js false, brand_isolved.js true —
 * same pattern as gUseBannerErrorAlert). This predicate is the single gate for
 * the entire feature (runtime card fork AND the Chart Settings section). Swap
 * the flag read for a feature/permission check when agent profiles become
 * per-account rather than per-brand.
 */
export const isAgentProfileEnabled = (): boolean => {
  return !!gAgentProfilesEnabled;
};

/**
 * DEV ONLY INS-7: the field-mapping pickers in the Agent profile settings
 * section exist for development against sample datasets. isolved ships
 * toggles-only (false): the field set is known ahead of time, so the mapping
 * is locked to the hard-coded defaults (getDefaultAgentHeadingConfig) and
 * users only control slot visibility. Flip to true to bring the pickers back
 * for development, or replace this flag with per-brand logic when other
 * brands (with unknown field sets) get agent profiles.
 */
export const kAgentDevShowFieldPickers = false;

/**
 * Canonical agent slot IDs. The ID doubles as (a) the persisted key in
 * profile.headingConfig.agent ({ fields, shownSlots }), (b) the
 * localization key for the settings row label, and (c) the default dataset
 * field name isolved sends (except the Alerts source slot, see defaultField).
 */
export const AgentSlotIds = {
  agentName: "Agent name",
  agentType: "Agent type",
  reportsTo: "Reports to",
  status: "Status",
  about: "About",
  department: "Department",
  detailLink: "Detail link",
  createdBy: "Created by",
  lastUpdated: "Last updated",
  modelUsed: "Model used",
  platform: "Platform",
  reviewFrequency: "Review frequency",
  accessScope: "Access scope",
  auditLogLink: "Audit log link",
  dateCreated: "Date created",
  dateDeployed: "Date deployed",
  errorsPrevented: "Errors prevented",
  autoCorrections: "Auto-corrections",
  itemsReviewed: "Items reviewed",
  timeSaved: "Time saved",
  activeUsers: "Active users",
  approvalRate: "Approval rate",
  daysSinceLastUse: "Days since last use",
  requestsMade: "Requests made",
  sessions: "Sessions",
  agentSpend: "Agent spend",
  tokenSpend: "Token spend",
  alerts: "Alerts",
  criticalAlerts: "Critical alerts",
  openAlerts: "Open alerts",
  topAlerts: "Top alerts",
  autoApplied: "Auto-applied",
  approved: "Approved",
  resolved: "Resolved",
  dismissed: "Dismissed"
} as const;

export type AgentSlotId = (typeof AgentSlotIds)[keyof typeof AgentSlotIds];

/**
 * INS-109: slots isolved asked us to hide. Suppression is applied once, at read
 * time in getVisibleAgentSlots, so the card sections, the settings groups and
 * the preview all drop them from this single list -- delete an entry to restore
 * that slot everywhere, with no other edit.
 *
 * Hidden, NOT removed: isolved still pushes every one of these columns, so the
 * card sections and AgentAlertList that render them are deliberately kept (they
 * return null while suppressed). Saved shownSlots are left untouched for the
 * same reason -- un-suppressing a slot restores the customer's stored on/off
 * preference rather than resetting it.
 */
export const AGENT_SUPPRESSED_SLOT_IDS: ReadonlySet<string> = new Set<string>([
  // Alerts section + settings group (incl. the JSON source slot).
  AgentSlotIds.alerts,
  AgentSlotIds.openAlerts,
  AgentSlotIds.criticalAlerts,
  AgentSlotIds.topAlerts,
  // Cost section + settings group.
  AgentSlotIds.agentSpend,
  AgentSlotIds.tokenSpend,
  // Adoption & activity section + settings group.
  AgentSlotIds.activeUsers,
  AgentSlotIds.approvalRate,
  AgentSlotIds.daysSinceLastUse,
  AgentSlotIds.requestsMade,
  AgentSlotIds.sessions,
  // Audit log link only -- the rest of Lifecycle and governance stays.
  AgentSlotIds.auditLogLink,
  // VIZ-125: isolved sends a reports-to ID, not a name. Delete once the name mapping ships.
  AgentSlotIds.reportsTo
]);

/** Settings-section group headings (values are the localization keys). */
export enum AgentSlotGroups {
  Identity = "Identity",
  Technical = "Technical",
  Lifecycle = "Lifecycle and governance",
  Performance = "Performance",
  Adoption = "Adoption and activity",
  Cost = "Cost",
  Alerts = "Alerts",
  ActionBreakdown = "Action breakdown"
}

export type AgentSlotDef = {
  id: AgentSlotId;
  group: AgentSlotGroups;
  /** Forced visible while available; settings toggle disabled (checked when available). */
  locked?: boolean;
  /** Renders a visibility Switch in settings. */
  hasToggle: boolean;
  /** Renders a dataset-field picker in settings (dev only, kAgentDevShowFieldPickers). */
  hasPicker: boolean;
  /** Default dataset field the slot reads when no mapping is stored. */
  defaultField: string;
  /**
   * Companion column holding a pre-computed percentage (Action breakdown only).
   * The card renders this verbatim rather than deriving a share from the counts:
   * the percentage is isolved's metric to define, and silently recomputing it
   * would both hide a data error and disagree with their own Guardian UI.
   */
  percentField?: string;
};

const slot = (
  id: AgentSlotId,
  group: AgentSlotGroups,
  overrides: Partial<Omit<AgentSlotDef, "id" | "group">> = {}
): AgentSlotDef => ({
  id,
  group,
  hasToggle: true,
  hasPicker: true,
  defaultField: id,
  ...overrides
});

/**
 * Ordered per the settings mock (order drives the settings grid).
 * defaultField values are the FLATTENED isolved guardian-agent payload columns
 * (confirmed payload 2026-09-30). isolved sends nested objects (identity, cost,
 * alerts, …); the ocAPI ingest flattens them with an underscore separator
 * (qutil.flatten_record), so e.g. cost.agentSpend.value → cost_agentSpend_value.
 * Structured stats (value/unit/period) map to their _value column; display
 * formatting ($, unit, /period) is handled by the chart field-format layer.
 *
 * This catalog is the single place to update if the payload shape changes;
 * saved profiles store only deltas (getAgentFieldOverrides), so edits here
 * reach every org with no migration.
 */
export const AGENT_SLOTS: AgentSlotDef[] = [
  slot(AgentSlotIds.agentName, AgentSlotGroups.Identity, {
    locked: true,
    defaultField: "identity_agentName"
  }),
  slot(AgentSlotIds.agentType, AgentSlotGroups.Identity, {
    locked: true,
    defaultField: "identity_agentType"
  }),
  slot(AgentSlotIds.reportsTo, AgentSlotGroups.Identity, {
    locked: true,
    defaultField: "identity_reportsTo"
  }),
  slot(AgentSlotIds.status, AgentSlotGroups.Identity, {
    locked: true,
    defaultField: "identity_status"
  }),
  slot(AgentSlotIds.about, AgentSlotGroups.Identity, { defaultField: "identity_about" }),
  slot(AgentSlotIds.department, AgentSlotGroups.Identity, { defaultField: "identity_department" }),
  slot(AgentSlotIds.detailLink, AgentSlotGroups.Identity, { defaultField: "identity_detailLink" }),
  slot(AgentSlotIds.createdBy, AgentSlotGroups.Identity, { defaultField: "identity_createdBy" }),
  slot(AgentSlotIds.lastUpdated, AgentSlotGroups.Technical, {
    defaultField: "technicalProfile_lastUpdated"
  }),
  slot(AgentSlotIds.modelUsed, AgentSlotGroups.Technical, {
    defaultField: "technicalProfile_modelUsed"
  }),
  slot(AgentSlotIds.platform, AgentSlotGroups.Technical, {
    defaultField: "technicalProfile_platform"
  }),
  slot(AgentSlotIds.reviewFrequency, AgentSlotGroups.Technical, {
    defaultField: "technicalProfile_reviewFrequency"
  }),
  // isolved renamed the section lifecycleAndGovernance -> lifecycleAndManagement (2026-10-08 payload).
  slot(AgentSlotIds.accessScope, AgentSlotGroups.Lifecycle, {
    defaultField: "lifecycleAndManagement_accessScope"
  }),
  slot(AgentSlotIds.auditLogLink, AgentSlotGroups.Lifecycle, {
    defaultField: "lifecycleAndManagement_auditLogLink"
  }),
  slot(AgentSlotIds.dateCreated, AgentSlotGroups.Lifecycle, {
    defaultField: "lifecycleAndManagement_dateCreated"
  }),
  slot(AgentSlotIds.dateDeployed, AgentSlotGroups.Lifecycle, {
    defaultField: "lifecycleAndManagement_dateDeployed"
  }),
  slot(AgentSlotIds.errorsPrevented, AgentSlotGroups.Performance, {
    defaultField: "performance_errorsPrevented"
  }),
  slot(AgentSlotIds.autoCorrections, AgentSlotGroups.Performance, {
    defaultField: "performance_autoCorrections"
  }),
  slot(AgentSlotIds.itemsReviewed, AgentSlotGroups.Performance, {
    defaultField: "performance_itemsReviewed_value"
  }),
  slot(AgentSlotIds.timeSaved, AgentSlotGroups.Performance, {
    defaultField: "performance_timeSaved_value"
  }),
  slot(AgentSlotIds.activeUsers, AgentSlotGroups.Adoption, {
    defaultField: "adoptionAndActivity_activeUsers"
  }),
  slot(AgentSlotIds.approvalRate, AgentSlotGroups.Adoption, {
    defaultField: "adoptionAndActivity_approvalRate"
  }),
  slot(AgentSlotIds.daysSinceLastUse, AgentSlotGroups.Adoption, {
    defaultField: "adoptionAndActivity_daysSinceLastUse"
  }),
  slot(AgentSlotIds.requestsMade, AgentSlotGroups.Adoption, {
    defaultField: "adoptionAndActivity_requestsMade"
  }),
  slot(AgentSlotIds.sessions, AgentSlotGroups.Adoption, {
    defaultField: "adoptionAndActivity_sessions_value"
  }),
  slot(AgentSlotIds.agentSpend, AgentSlotGroups.Cost, { defaultField: "cost_agentSpend_value" }),
  slot(AgentSlotIds.tokenSpend, AgentSlotGroups.Cost, { defaultField: "cost_tokenSpend_value" }),
  // Alerts: isolved sends alerts.{openAlerts,criticalAlerts,topAlerts[]}, which
  // flatten to alerts_openAlerts / alerts_criticalAlerts (counts) and
  // alerts_topAlerts (a JSON-string array of {title,severity,affectedEmployeeCount,
  // guardianLink}). resolveAgentAlerts reads the counts directly and parses the
  // JSON array for the rows; "Top alerts" is the rows' visibility toggle.
  slot(AgentSlotIds.alerts, AgentSlotGroups.Alerts, {
    hasToggle: false,
    defaultField: "alerts_topAlerts"
  }),
  slot(AgentSlotIds.criticalAlerts, AgentSlotGroups.Alerts, {
    defaultField: "alerts_criticalAlerts"
  }),
  slot(AgentSlotIds.openAlerts, AgentSlotGroups.Alerts, { defaultField: "alerts_openAlerts" }),
  slot(AgentSlotIds.topAlerts, AgentSlotGroups.Alerts, { hasPicker: false }),
  slot(AgentSlotIds.autoApplied, AgentSlotGroups.ActionBreakdown, {
    defaultField: "actionBreakdown_autoApplied_count",
    percentField: "actionBreakdown_autoApplied_percentage"
  }),
  slot(AgentSlotIds.approved, AgentSlotGroups.ActionBreakdown, {
    defaultField: "actionBreakdown_approved_count",
    percentField: "actionBreakdown_approved_percentage"
  }),
  slot(AgentSlotIds.resolved, AgentSlotGroups.ActionBreakdown, {
    defaultField: "actionBreakdown_resolved_count",
    percentField: "actionBreakdown_resolved_percentage"
  }),
  slot(AgentSlotIds.dismissed, AgentSlotGroups.ActionBreakdown, {
    defaultField: "actionBreakdown_dismissed_count",
    percentField: "actionBreakdown_dismissed_percentage"
  })
];

const slotById = new Map<string, AgentSlotDef>(AGENT_SLOTS.map(def => [def.id, def]));

export const AGENT_LOCKED_SLOT_IDS: AgentSlotId[] = AGENT_SLOTS.filter(def => def.locked).map(
  def => def.id
);

export const AGENT_TOGGLEABLE_SLOT_IDS: AgentSlotId[] = AGENT_SLOTS.filter(
  def => def.hasToggle
).map(def => def.id);

/** Flat slot → dataset-field map (the `fields` half of AgentProfileSettings). */
export type AgentHeadingConfig = Record<string, string>;

/**
 * Everything the agent card persists, stored as ONE object at
 * profile.headingConfig.agent — the heading config already flows from settings
 * to the runtime card (QProfileMgr.fHeadingConfig / UpdateHeadingConfig), so no
 * extra plumbing is needed.
 */
export type AgentProfileSettings = {
  /** Slot → dataset field overrides; missing/blank entries use the defaults. */
  fields?: AgentHeadingConfig | null;
  /** Visible slot IDs; missing ⇒ everything visible (all-on default). */
  shownSlots?: string[] | null;
};

/** Safe accessor for the agent settings stored under a headingConfig object. */
export const getAgentSettingsFromHeadingConfig = (headingConfig: unknown): AgentProfileSettings => {
  const agent = (headingConfig as Record<string, any> | null | undefined)?.agent;
  if (agent == null || typeof agent !== "object" || Array.isArray(agent)) {
    return {};
  }
  return {
    fields:
      agent.fields != null && typeof agent.fields === "object" && !Array.isArray(agent.fields)
        ? agent.fields
        : null,
    shownSlots: Array.isArray(agent.shownSlots) ? agent.shownSlots : null
  };
};

export const getDefaultAgentHeadingConfig = (): AgentHeadingConfig => {
  return Object.fromEntries(
    AGENT_SLOTS.filter(def => def.hasPicker).map(def => [def.id, def.defaultField])
  );
};

/**
 * Reduces a full slot→field map to only the entries that differ from the
 * hard-coded defaults. This is what gets PERSISTED: storing deltas instead of
 * snapshots keeps the default mapping live-swappable — updating
 * AGENT_SLOTS[].defaultField reaches every org that never explicitly remapped
 * that slot. Blank values (= "use the default") are dropped too.
 */
export const getAgentFieldOverrides = (
  fields: AgentHeadingConfig | null | undefined
): AgentHeadingConfig => {
  if (fields == null) {
    return {};
  }
  const overrides: AgentHeadingConfig = {};
  for (const [slotId, fieldName] of Object.entries(fields)) {
    if (fieldName && fieldName !== (slotById.get(slotId)?.defaultField ?? slotId)) {
      overrides[slotId] = fieldName;
    }
  }
  return overrides;
};

/**
 * Dataset field a slot reads: the stored mapping when present, otherwise the
 * slot's default field.
 */
export const getAgentSlotFieldName = (
  config: AgentHeadingConfig | null | undefined,
  slotId: AgentSlotId
): string => {
  const mapped = config?.[slotId];
  if (typeof mapped === "string" && mapped.length > 0) {
    return mapped;
  }
  return slotById.get(slotId)?.defaultField ?? slotId;
};

/** Companion percentage column for a slot, when the catalog defines one. */
export const getAgentSlotPercentField = (slotId: AgentSlotId): string | undefined => {
  return slotById.get(slotId)?.percentField;
};

/**
 * A slot is available iff its resolved field exists in the chart metadata
 * (alert slots fall back across their sources, like resolveAgentAlerts).
 * A column pushed blank never reaches here: ocAPI drops it from the agent
 * record (INS-105), so it is simply absent, exactly like an unpushed column.
 * Read-time only — never persisted, so re-enabled fields restore slots.
 * Membership semantics are the caller's: the card probes GetFieldDefByID
 * (fuzzy) while settings checks the headers pool (exact) — tolerated
 * divergence, only visible for non-verbatim column casing.
 */
export const getAvailableAgentSlots = (
  hasField: (fieldName: string) => boolean,
  config: AgentHeadingConfig | null | undefined
): Set<string> => {
  const slotFieldExists = (slotId: AgentSlotId) => hasField(getAgentSlotFieldName(config, slotId));

  const available = new Set<string>();
  for (const def of AGENT_SLOTS) {
    if (def.id === AgentSlotIds.openAlerts || def.id === AgentSlotIds.criticalAlerts) {
      if (slotFieldExists(def.id) || slotFieldExists(AgentSlotIds.alerts)) {
        available.add(def.id);
      }
    } else if (def.id === AgentSlotIds.topAlerts) {
      if (slotFieldExists(AgentSlotIds.alerts)) {
        available.add(def.id);
      }
    } else if (slotFieldExists(def.id)) {
      available.add(def.id);
    }
  }
  return available;
};

/**
 * Visible slot IDs from profile.headingConfig.agent.shownSlots. Missing list ⇒
 * everything visible; locked slots forced in; availability intersects next
 * (an unpushed field hides its slot regardless of preferences or locks), and
 * AGENT_SUPPRESSED_SLOT_IDS strips last so suppression beats locks too.
 */
export const getVisibleAgentSlots = (
  included: string[] | null | undefined,
  available?: Set<string> | null
): Set<string> => {
  const visible = new Set<string>(included ?? AGENT_TOGGLEABLE_SLOT_IDS);
  for (const id of AGENT_LOCKED_SLOT_IDS) {
    visible.add(id);
  }
  if (available) {
    for (const id of [...visible]) {
      if (!available.has(id)) {
        visible.delete(id);
      }
    }
  }
  for (const id of AGENT_SUPPRESSED_SLOT_IDS) {
    visible.delete(id);
  }
  return visible;
};

/** Card sections in render order. Card sections ≠ settings groups (About mixes Identity + Technical slots). */
export enum AgentCardSections {
  Header = "header",
  About = "about",
  Lifecycle = "lifecycle",
  Performance = "performance",
  Adoption = "adoption",
  Cost = "cost",
  Alerts = "alerts",
  ActionBreakdown = "actionBreakdown"
}

export const AGENT_CARD_SECTIONS: Record<AgentCardSections, AgentSlotId[]> = {
  [AgentCardSections.Header]: [
    AgentSlotIds.agentName,
    AgentSlotIds.agentType,
    AgentSlotIds.reportsTo,
    AgentSlotIds.status,
    AgentSlotIds.department,
    AgentSlotIds.createdBy
  ],
  [AgentCardSections.About]: [
    AgentSlotIds.about,
    AgentSlotIds.detailLink,
    AgentSlotIds.lastUpdated,
    AgentSlotIds.reviewFrequency,
    AgentSlotIds.modelUsed,
    AgentSlotIds.platform
  ],
  [AgentCardSections.Lifecycle]: [
    AgentSlotIds.dateCreated,
    AgentSlotIds.dateDeployed,
    AgentSlotIds.accessScope,
    AgentSlotIds.auditLogLink
  ],
  [AgentCardSections.Performance]: [
    AgentSlotIds.errorsPrevented,
    AgentSlotIds.autoCorrections,
    AgentSlotIds.timeSaved,
    AgentSlotIds.itemsReviewed
  ],
  [AgentCardSections.Adoption]: [
    AgentSlotIds.activeUsers,
    AgentSlotIds.sessions,
    AgentSlotIds.requestsMade,
    AgentSlotIds.approvalRate,
    AgentSlotIds.daysSinceLastUse
  ],
  [AgentCardSections.Cost]: [AgentSlotIds.agentSpend, AgentSlotIds.tokenSpend],
  [AgentCardSections.Alerts]: [
    AgentSlotIds.openAlerts,
    AgentSlotIds.criticalAlerts,
    AgentSlotIds.topAlerts
  ],
  [AgentCardSections.ActionBreakdown]: [
    AgentSlotIds.autoApplied,
    AgentSlotIds.approved,
    AgentSlotIds.resolved,
    AgentSlotIds.dismissed
  ]
};

/** A card section renders iff at least one of its composed slots is visible. */
export const isAgentSectionVisible = (
  section: AgentCardSections,
  visibleSlots: Set<string>
): boolean => {
  return AGENT_CARD_SECTIONS[section].some(slotId => visibleSlots.has(slotId));
};

/** Kebab-case slug for test IDs (Pendo-tracked): "Agent name" → "agent-name". */
export const getAgentSlotTestSlug = (slotId: string): string => {
  return slotId
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/**
 * Sanitizes a dataset-supplied link value before it reaches an href: the CRLS
 * "__blocked__" sentinel and non-http(s) schemes (javascript:, data:, …)
 * resolve to "" so the link simply doesn't render. Same-page ("#…") and
 * root-relative ("/…") values stay allowed for canned previews and
 * portal-relative URLs.
 */
export const sanitizeAgentLinkValue = (value: unknown): string => {
  if (typeof value !== "string" || isBlockedField(value)) {
    return "";
  }
  const trimmed = value.trim();
  return /^(https?:\/\/|\/|#)/i.test(trimmed) ? trimmed : "";
};

export type AgentTopAlert = {
  title: string;
  severity: string;
  affectedEmployeeCount: number | null;
  guardianLink: string;
};

export type AgentAlertsData = {
  openAlerts: number | null;
  criticalAlerts: number | null;
  topAlerts: AgentTopAlert[];
};

const toCount = (value: unknown): number | null => {
  // Whitespace-only counts as missing — Number(" ") is 0, which would render
  // a false zero count in the Alerts tiles.
  if (value == null || (typeof value === "string" && !value.trim())) {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Tolerant parse of the isolved alerts column. Accepts a JSON string or an
 * already-parsed object, with or without the { alerts: { ... } } wrapper.
 * Returns null for blank/malformed values so the card degrades gracefully.
 */
export const parseAgentAlerts = (rawValue: unknown): AgentAlertsData | null => {
  let parsed: unknown = rawValue;

  if (typeof parsed === "string") {
    const trimmed = parsed.trim();
    if (!trimmed) {
      return null;
    }
    try {
      parsed = JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  if (parsed == null || typeof parsed !== "object") {
    return null;
  }

  // Accept a bare topAlerts array (isolved's flattened alerts_topAlerts column),
  // an { alerts: {...} } wrapper, or a flat { openAlerts, …, topAlerts } object.
  let source: Record<string, unknown>;
  if (Array.isArray(parsed)) {
    source = { topAlerts: parsed };
  } else {
    const wrapper = parsed as Record<string, unknown>;
    const inner = wrapper.alerts;
    source = (
      inner != null && typeof inner === "object" && !Array.isArray(inner) ? inner : wrapper
    ) as Record<string, unknown>;
  }

  const topAlerts: AgentTopAlert[] = Array.isArray(source.topAlerts)
    ? (source.topAlerts as unknown[])
        .filter((entry): entry is Record<string, unknown> => {
          return entry != null && typeof entry === "object" && !Array.isArray(entry);
        })
        .map(entry => ({
          title: typeof entry.title === "string" ? entry.title : "",
          severity: typeof entry.severity === "string" ? entry.severity.toLowerCase() : "",
          affectedEmployeeCount: toCount(entry.affectedEmployeeCount),
          guardianLink: sanitizeAgentLinkValue(entry.guardianLink)
        }))
    : [];

  return {
    openAlerts: toCount(source.openAlerts),
    criticalAlerts: toCount(source.criticalAlerts),
    topAlerts
  };
};

/**
 * Single resolution point for the Alerts section: the flattened numeric count
 * columns (alerts_openAlerts / alerts_criticalAlerts) win for the tiles, and
 * the alerts_topAlerts JSON array (parsed via parseAgentAlerts) supplies the
 * rows. Returns null when no alert data exists at all.
 */
export const resolveAgentAlerts = (
  record: Record<string, any> | null | undefined,
  config: AgentHeadingConfig | null | undefined
): AgentAlertsData | null => {
  const readSlot = (slotId: AgentSlotId) => record?.[getAgentSlotFieldName(config, slotId)];

  const jsonData = parseAgentAlerts(readSlot(AgentSlotIds.alerts));
  const openAlerts = toCount(readSlot(AgentSlotIds.openAlerts)) ?? jsonData?.openAlerts ?? null;
  const criticalAlerts =
    toCount(readSlot(AgentSlotIds.criticalAlerts)) ?? jsonData?.criticalAlerts ?? null;
  const topAlerts = jsonData?.topAlerts ?? [];

  if (openAlerts == null && criticalAlerts == null && topAlerts.length === 0) {
    return null;
  }
  return { openAlerts, criticalAlerts, topAlerts };
};

// Mirrors the isolved guardian-agent sample payload (confirmed 2026-09-30),
// after ocAPI flattening. Structured stats hold their raw _value (display
// formatting comes from the chart field-format layer); the alerts source holds
// the flattened alerts_topAlerts JSON array.
const AGENT_SAMPLE_VALUES: Record<string, string> = {
  [AgentSlotIds.agentName]: "The Guardian",
  [AgentSlotIds.agentType]: "Payroll Agent",
  [AgentSlotIds.reportsTo]: "Sofia Petrov (Dir., Payroll Processing)",
  [AgentSlotIds.status]: "Active",
  [AgentSlotIds.about]:
    "Reviews every payroll run before processing to catch discrepancies in hours, deductions, and tax withholding. Flags anomalies for human approval and auto-corrects low-risk formatting errors, such as mismatched pay period dates.",
  [AgentSlotIds.department]: "Human Resources",
  [AgentSlotIds.detailLink]: "",
  [AgentSlotIds.createdBy]: "Sofia Petrov (Dir., Payroll Processing)",
  [AgentSlotIds.lastUpdated]: "2026-07-28",
  [AgentSlotIds.modelUsed]: "GPT-4.1, fine-tuned on payroll policy rules",
  [AgentSlotIds.platform]: "Bedrock",
  [AgentSlotIds.reviewFrequency]: "(biweekly) Every pay run",
  [AgentSlotIds.accessScope]: "Assisted",
  [AgentSlotIds.auditLogLink]: "",
  [AgentSlotIds.dateCreated]: "2025-01-12",
  [AgentSlotIds.dateDeployed]: "2025-02-03",
  [AgentSlotIds.errorsPrevented]: "1284",
  [AgentSlotIds.autoCorrections]: "214",
  [AgentSlotIds.itemsReviewed]: "3420",
  [AgentSlotIds.timeSaved]: "128",
  [AgentSlotIds.activeUsers]: "12",
  [AgentSlotIds.approvalRate]: "94",
  [AgentSlotIds.daysSinceLastUse]: "2",
  [AgentSlotIds.requestsMade]: "189",
  [AgentSlotIds.sessions]: "47",
  [AgentSlotIds.agentSpend]: "340",
  [AgentSlotIds.tokenSpend]: "1800000",
  [AgentSlotIds.openAlerts]: "3",
  [AgentSlotIds.criticalAlerts]: "1",
  [AgentSlotIds.alerts]: JSON.stringify([
    {
      title: "Duplicate direct deposit account detected",
      severity: "critical",
      affectedEmployeeCount: 1,
      guardianLink: ""
    },
    {
      title: "Overtime hours exceed policy threshold",
      severity: "warning",
      affectedEmployeeCount: 3,
      guardianLink: ""
    },
    {
      title: "Missing state tax withholding form",
      severity: "warning",
      affectedEmployeeCount: 2,
      guardianLink: ""
    }
  ]),
  [AgentSlotIds.autoApplied]: "640",
  [AgentSlotIds.approved]: "310",
  [AgentSlotIds.resolved]: "155",
  [AgentSlotIds.dismissed]: "155"
};

// Action breakdown percentages for the settings preview. Kept internally
// consistent (summing to 100) because this is OUR illustrative fixture -- a
// preview that advertised a real payload's arithmetic error would read as a bug
// in the card. The live card renders isolved's pushed values verbatim.
const AGENT_SAMPLE_PERCENT_VALUES: Record<string, string> = {
  [AgentSlotIds.autoApplied]: "51",
  [AgentSlotIds.approved]: "25",
  [AgentSlotIds.resolved]: "12",
  [AgentSlotIds.dismissed]: "12"
};

/**
 * Canned record (mock values) keyed by each slot's currently-mapped field
 * name — used by the settings Preview, which has no real agent record to look
 * up (LookupRecord resolves by box ID, not by isAgent).
 */
export const buildAgentSampleRecord = (
  config?: AgentHeadingConfig | null
): Record<string, string> => {
  const record: Record<string, string> = {};
  for (const def of AGENT_SLOTS) {
    if (!def.hasPicker) {
      continue;
    }
    const sampleValue = AGENT_SAMPLE_VALUES[def.id];
    if (sampleValue != null) {
      record[getAgentSlotFieldName(config, def.id)] = sampleValue;
    }
    const samplePercent = AGENT_SAMPLE_PERCENT_VALUES[def.id];
    if (def.percentField && samplePercent != null) {
      record[def.percentField] = samplePercent;
    }
  }
  return record;
};
